import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  HandCoins, Plus, X, Loader2, Building2, Clock, CheckCircle2,
  XCircle, AlertCircle, Calendar, DollarSign, Infinity as InfinityIcon,
  Trash2, Send, Zap, KeyRound, Copy, BookOpen, ListChecks
} from 'lucide-react';
import { toast } from 'sonner';
import { PartnerSponsorshipGuide } from './PartnerSponsorshipGuide';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending_approval: { label: 'Aguardando aprovação', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  active: { label: 'Ativo', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  cancelled: { label: 'Cancelado', color: 'bg-slate-100 text-slate-500 border-slate-200', icon: XCircle },
  expired: { label: 'Expirado', color: 'bg-slate-100 text-slate-500 border-slate-200', icon: Clock },
  rejected: { label: 'Rejeitado', color: 'bg-rose-100 text-rose-700 border-rose-200', icon: XCircle },
  suspended: { label: 'Suspenso (inadimplência)', color: 'bg-rose-100 text-rose-700 border-rose-200', icon: XCircle },
};

const formatBR = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('pt-BR'); } catch { return '—'; }
};

const formatMoney = (v) => `R$ ${(v ?? 0).toFixed(2).replace('.', ',')}`;

export const PartnerSponsorshipsTab = ({ token, partnerName, initialView }) => {
  const [view, setView] = useState(initialView === 'guia' ? 'guia' : 'operacional');
  const [data, setData] = useState({ items: [], summary: {} });
  const [config, setConfig] = useState({ auto_approve: false, blocked: false, monthly_price: 49.9 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    company_name: '', cnpj: '', owner_name: '', owner_email: '', owner_phone: '',
    duration_months: '12', notes: ''
  });
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelWhen, setCancelWhen] = useState('end_of_cycle');
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [credentials, setCredentials] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [spRes, cfgRes] = await Promise.all([
        fetch(`${API}/partners/panel/${token}/sponsorships`).then(r => r.ok ? r.json() : { items: [], summary: {} }),
        fetch(`${API}/partners/panel/${token}/sponsorship-config`).then(r => r.ok ? r.json() : {}),
      ]);
      setData(spRes);
      setConfig(cfgRes);
    } catch {
      toast.error('Erro ao carregar patrocínios');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim() || !form.cnpj.trim() || !form.owner_email.trim() || !form.owner_name.trim() || !form.owner_phone.trim()) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        duration_months: form.duration_months === 'indefinite' ? null : parseInt(form.duration_months, 10),
      };
      const res = await fetch(`${API}/partners/panel/${token}/sponsorships/request`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'fail');
      }
      const result = await res.json();
      if (result.auto_approved) {
        // Fluxo turbo: credenciais ja prontas
        toast.success('Patrocínio criado com sucesso!');
        setCredentials({
          email: result.credentials?.email,
          password: result.credentials?.password,
          login_url: result.credentials?.login_url || 'https://www.falocomodono.com.br/login',
          company: payload.company_name,
          existing_user: result.existing_user,
        });
      } else {
        toast.success('Solicitação enviada! Aguarde a aprovação do administrador.');
      }
      setShowForm(false);
      setForm({ company_name: '', cnpj: '', owner_name: '', owner_email: '', owner_phone: '', duration_months: '12', notes: '' });
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Erro ao enviar solicitação');
    } finally { setSubmitting(false); }
  };

  const handleViewCredentials = async (sp) => {
    try {
      const res = await fetch(`${API}/partners/panel/${token}/sponsorships/${sp.id}/credentials`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setCredentials({
        email: data.email,
        password: data.password,
        login_url: data.login_url,
        company: sp.sponsored_company_name,
        existing_user: data.has_existing_user,
      });
    } catch {
      toast.error('Erro ao buscar credenciais');
    }
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const res = await fetch(`${API}/partners/panel/${token}/sponsorships/${cancelTarget.id}/cancel`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ when: cancelWhen, reason: cancelReason }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'fail');
      }
      toast.success(
        cancelWhen === 'now'
          ? 'Patrocínio cancelado. O cliente caiu para o plano Free.'
          : 'Patrocínio cancelado ao fim do ciclo. Sem novas faturas.'
      );
      setCancelTarget(null); setCancelReason(''); setCancelWhen('end_of_cycle');
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Erro ao cancelar');
    } finally { setCancelling(false); }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#C5A059]" /></div>;
  }

  const { items = [], summary = {} } = data;
  const overdueCount = items.filter(s => s.status === 'active' && s.payment_status === 'overdue').length;

  return (
    <main className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6" data-testid="partner-sponsorships-tab">
      {/* Toggle Operacional / Guia */}
      <div className="flex gap-2 bg-slate-100 rounded-xl p-1 w-fit print:hidden">
        <button
          onClick={() => setView('operacional')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1.5 ${
            view === 'operacional' ? 'bg-white text-[#0A2647] shadow-sm' : 'text-slate-600 hover:text-[#0A2647]'
          }`}
          data-testid="view-operacional"
        >
          <ListChecks className="w-4 h-4" /> Operacional
        </button>
        <button
          onClick={() => setView('guia')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1.5 ${
            view === 'guia' ? 'bg-white text-[#0A2647] shadow-sm' : 'text-slate-600 hover:text-[#0A2647]'
          }`}
          data-testid="view-guia"
        >
          <BookOpen className="w-4 h-4" /> Guia · Como funciona
        </button>
      </div>

      {view === 'guia' && (
        <PartnerSponsorshipGuide
          partnerName={partnerName || 'Sua Empresa Parceira'}
          monthlyPrice={config.monthly_price}
          autoApprove={config.auto_approve}
        />
      )}

      {view === 'operacional' && (
      <>
      {/* Cabeçalho explicativo */}
      <Card className="border-0 bg-gradient-to-br from-[#0A2647] to-[#0e3260] shadow-lg">
        <CardContent className="pt-6 pb-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#C5A059]/20 flex items-center justify-center flex-shrink-0">
              <HandCoins className="w-6 h-6 text-[#C5A059]" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-white mb-1">Patrocinar Clientes</h2>
              <p className="text-sm text-slate-300 leading-relaxed">
                Você paga uma mensalidade fixa por cliente e ele recebe o <strong>Plano 5 Estrelas</strong> sem cobrança direta.
                Cada patrocínio gera uma cobrança individual no Asaas em seu nome — você pode cancelar a qualquer momento por aqui.
              </p>
              {config.auto_approve && !config.blocked && (
                <div className="mt-3 inline-flex items-center gap-2 bg-emerald-500/15 border border-emerald-400/30 rounded-lg px-3 py-1.5">
                  <Zap className="w-4 h-4 text-emerald-300" />
                  <span className="text-xs font-bold text-emerald-200">
                    Aprovação automática · {formatMoney(config.monthly_price)}/mês por cliente · credenciais na hora
                  </span>
                </div>
              )}
              {config.blocked && (
                <div className="mt-3 inline-flex items-center gap-2 bg-rose-500/15 border border-rose-400/30 rounded-lg px-3 py-1.5">
                  <AlertCircle className="w-4 h-4 text-rose-300" />
                  <span className="text-xs font-bold text-rose-200">
                    Patrocínios temporariamente indisponíveis. Entre em contato com o administrador.
                  </span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {overdueCount > 0 && (
        <Card className="border-2 border-rose-300 bg-rose-50 shadow-md" data-testid="overdue-banner">
          <CardContent className="pt-5 pb-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-rose-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold text-rose-900">
                  {overdueCount === 1 ? '1 fatura de patrocínio em atraso' : `${overdueCount} faturas de patrocínio em atraso`}
                </p>
                <p className="text-xs text-rose-700 mt-1 leading-relaxed">
                  Regularize no seu painel Asaas. Após 15 dias de atraso, o patrocínio é suspenso automaticamente e o cliente cai para o plano Free. <strong>O cliente não foi avisado do atraso.</strong>
                </p>
                <a
                  href="https://www.asaas.com/customerArea"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-md"
                  data-testid="open-asaas-btn"
                >
                  Abrir Asaas e pagar agora
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resumo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 bg-white shadow-md">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Ativos</p>
            <p className="text-2xl font-bold text-emerald-600" data-testid="summary-active">{summary.active || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-0 bg-white shadow-md">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Aguardando</p>
            <p className="text-2xl font-bold text-amber-600" data-testid="summary-pending">{summary.pending || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-0 bg-white shadow-md">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Total / mês</p>
            <p className="text-2xl font-bold text-[#0A2647]" data-testid="summary-cost">{formatMoney(summary.total_monthly_cost)}</p>
          </CardContent>
        </Card>
        <Card className="border-0 bg-white shadow-md">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Cancelados</p>
            <p className="text-2xl font-bold text-slate-400" data-testid="summary-cancelled">{summary.cancelled || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Botão / Form */}
      {!showForm ? (
        <Button
          onClick={() => setShowForm(true)}
          disabled={config.blocked}
          className="bg-[#C5A059] hover:bg-[#b08e4a] text-white gap-2 h-12 px-6 font-semibold disabled:opacity-50"
          data-testid="new-sponsorship-btn"
        >
          <Plus className="w-4 h-4" /> Patrocinar novo cliente
        </Button>
      ) : (
        <Card className="border-2 border-[#C5A059]/30 bg-white shadow-md">
          <CardContent className="pt-6 pb-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-[#0A2647]">Novo Patrocínio</h3>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-slate-100 rounded" data-testid="close-form-btn">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Nome da empresa <span className="text-rose-500">*</span></Label>
                  <Input value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} placeholder="Ex: Pizzaria Bella Napoli" className="mt-1" data-testid="form-company-name" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700">CNPJ <span className="text-rose-500">*</span></Label>
                  <Input value={form.cnpj} onChange={e => setForm({ ...form, cnpj: e.target.value })} placeholder="00.000.000/0000-00" className="mt-1" data-testid="form-cnpj" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Nome do dono <span className="text-rose-500">*</span></Label>
                  <Input value={form.owner_name} onChange={e => setForm({ ...form, owner_name: e.target.value })} placeholder="Ex: Maria Silva" className="mt-1" data-testid="form-owner-name" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700">WhatsApp do dono <span className="text-rose-500">*</span></Label>
                  <Input value={form.owner_phone} onChange={e => setForm({ ...form, owner_phone: e.target.value })} placeholder="(11) 99999-9999" className="mt-1" data-testid="form-owner-phone" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">E-mail do dono (login) <span className="text-rose-500">*</span></Label>
                <Input type="email" value={form.owner_email} onChange={e => setForm({ ...form, owner_email: e.target.value })} placeholder="maria@bellanapoli.com.br" className="mt-1" data-testid="form-owner-email" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Duração do patrocínio</Label>
                <select
                  value={form.duration_months}
                  onChange={e => setForm({ ...form, duration_months: e.target.value })}
                  className="mt-1 w-full h-9 px-3 border border-slate-200 rounded-md text-sm bg-white"
                  data-testid="form-duration"
                >
                  <option value="3">3 meses</option>
                  <option value="6">6 meses</option>
                  <option value="12">12 meses (1 ano)</option>
                  <option value="24">24 meses (2 anos)</option>
                  <option value="indefinite">Indefinido (até eu cancelar)</option>
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Observação (opcional)</Label>
                <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Ex: Cliente que conheci na feira de gastronomia" className="mt-1" rows={2} data-testid="form-notes" />
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2 text-xs text-amber-900">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p>
                  {config.auto_approve ? (
                    <>Após o envio, o sistema <strong>cria a conta na hora</strong> e mostra as credenciais aqui mesmo pra você entregar ao cliente. A primeira fatura vem no seu Asaas em até 24h.</>
                  ) : (
                    <>Após o envio, o <strong>administrador irá analisar e aprovar</strong>. Quando aprovado, as credenciais aparecem aqui no seu painel pra você entregar ao cliente.</>
                  )}
                </p>
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-[#0A2647] hover:bg-[#0e3260] text-white gap-2 h-11 font-semibold" data-testid="submit-sponsorship">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Enviar solicitação
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Lista */}
      {items.length === 0 ? (
        <Card className="border-0 bg-white shadow-md">
          <CardContent className="py-12 text-center">
            <HandCoins className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">Nenhum patrocínio ainda. Clique em "Patrocinar novo cliente" para começar.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map(s => {
            const meta = STATUS_META[s.status] || STATUS_META.pending_approval;
            const Icon = meta.icon;
            const isOverdue = s.status === 'active' && s.payment_status === 'overdue';
            return (
              <Card key={s.id} className={`border-0 bg-white shadow-md hover:shadow-lg transition-shadow ${isOverdue ? 'ring-2 ring-rose-300' : ''}`} data-testid={`sponsorship-${s.id}`}>
                <CardContent className="pt-5 pb-4">
                  <div className="flex flex-wrap items-start gap-3 justify-between">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-11 h-11 rounded-lg bg-[#C5A059]/15 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-5 h-5 text-[#C5A059]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-base font-bold text-[#0A2647] truncate">{s.sponsored_company_name}</p>
                        <p className="text-xs text-slate-500 truncate">{s.owner_name} · {s.owner_email}</p>
                        <div className="flex items-center gap-3 mt-2 flex-wrap text-xs text-slate-600">
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {s.duration_months ? `${s.duration_months} meses` : (<><InfinityIcon className="w-3 h-3" /> Indefinido</>)}
                          </span>
                          {s.monthly_price ? (
                            <span className="inline-flex items-center gap-1 font-semibold text-[#0A2647]">
                              <DollarSign className="w-3 h-3" /> {formatMoney(s.monthly_price)}/mês
                            </span>
                          ) : null}
                          {s.expires_at && (
                            <span className="text-slate-500">Vence em {formatBR(s.expires_at)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-semibold ${meta.color}`}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </span>
                      {isOverdue && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-rose-300 bg-rose-50 text-rose-700 text-[10px] font-bold uppercase">
                          <AlertCircle className="w-3 h-3" /> Fatura em atraso
                        </span>
                      )}
                      {(s.status === 'active' || s.status === 'pending_approval') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setCancelTarget(s); setCancelWhen('end_of_cycle'); setCancelReason(''); }}
                          className="text-rose-600 border-rose-200 hover:bg-rose-50 gap-1 h-7 text-xs"
                          data-testid={`cancel-btn-${s.id}`}
                        >
                          <Trash2 className="w-3 h-3" /> Cancelar
                        </Button>
                      )}
                      {s.status === 'active' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewCredentials(s)}
                          className="text-[#C5A059] border-[#C5A059]/30 hover:bg-[#C5A059]/10 gap-1 h-7 text-xs"
                          data-testid={`view-credentials-${s.id}`}
                        >
                          <KeyRound className="w-3 h-3" /> Ver credenciais
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal Credenciais (auto-aprovacao ou ver depois) */}
      {credentials && (
        <div className="fixed inset-0 z-[101] bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="flex-1">
                <p className="text-base font-bold text-[#0A2647]">Patrocínio ativo!</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Entregue estas credenciais ao seu cliente <strong>{credentials.company}</strong>:
                </p>
              </div>
            </div>

            {credentials.existing_user ? (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-900">
                  Este e-mail já tinha uma conta no Falo com o Dono. A empresa foi <strong>vinculada à conta existente</strong>. O cliente acessa com a senha atual dele.
                </p>
                <p className="text-xs text-blue-700 mt-2 font-mono break-all">{credentials.email}</p>
              </div>
            ) : (
              <div className="bg-slate-50 border-2 border-dashed border-slate-300 rounded-xl p-4 space-y-3">
                <div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">E-mail (login)</p>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-mono font-semibold text-slate-900 flex-1 break-all" data-testid="partner-cred-email">{credentials.email}</p>
                    <Button size="sm" variant="outline" className="h-7 px-2 gap-1 text-xs" onClick={() => { navigator.clipboard.writeText(credentials.email); toast.success('E-mail copiado'); }}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Senha</p>
                  <div className="flex items-center gap-2">
                    <p className="text-base font-mono font-bold text-[#C5A059] flex-1" data-testid="partner-cred-password">{credentials.password}</p>
                    <Button size="sm" variant="outline" className="h-7 px-2 gap-1 text-xs" onClick={() => { navigator.clipboard.writeText(credentials.password); toast.success('Senha copiada'); }}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Link de acesso</p>
                  <p className="text-xs font-mono text-slate-700">{credentials.login_url}</p>
                </div>
              </div>
            )}

            {!credentials.existing_user && (
              <Button
                onClick={() => {
                  const text = `${credentials.company} - Falo com o Dono\n\nE-mail: ${credentials.email}\nSenha: ${credentials.password}\nAcesso: ${credentials.login_url}\n\nVocê está no Plano 5 Estrelas patrocinado, sem custo. Acesse e configure sua loja!`;
                  navigator.clipboard.writeText(text);
                  toast.success('Mensagem copiada para enviar ao cliente');
                }}
                className="w-full bg-[#0A2647] hover:bg-[#0e3260] text-white gap-2"
                data-testid="copy-message-cred"
              >
                <Copy className="w-4 h-4" /> Copiar mensagem pronta para o cliente
              </Button>
            )}
            <Button variant="outline" onClick={() => setCredentials(null)} className="w-full" data-testid="close-credentials-modal">
              Fechar
            </Button>
          </div>
        </div>
      )}

      {/* Modal cancelar */}
      {cancelTarget && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
            <div>
              <p className="text-xs font-bold text-rose-600 uppercase tracking-wider mb-1">Cancelar patrocínio</p>
              <h3 className="text-base font-bold text-[#0A2647]">{cancelTarget.sponsored_company_name}</h3>
            </div>

            <div>
              <p className="text-xs font-semibold text-slate-700 mb-2">Quando cancelar?</p>
              <div className="space-y-2">
                <label className={`flex items-start gap-2 p-3 rounded-lg border-2 cursor-pointer ${cancelWhen === 'end_of_cycle' ? 'border-[#C5A059] bg-amber-50/50' : 'border-slate-200'}`}>
                  <input type="radio" name="when" checked={cancelWhen === 'end_of_cycle'} onChange={() => setCancelWhen('end_of_cycle')} className="mt-0.5" data-testid="cancel-end-cycle" />
                  <div>
                    <p className="text-sm font-semibold text-slate-800">No fim do ciclo atual</p>
                    <p className="text-xs text-slate-500">Você não recebe a próxima fatura. Cliente fica ativo até o vencimento já pago.</p>
                  </div>
                </label>
                <label className={`flex items-start gap-2 p-3 rounded-lg border-2 cursor-pointer ${cancelWhen === 'now' ? 'border-rose-500 bg-rose-50/50' : 'border-slate-200'}`}>
                  <input type="radio" name="when" checked={cancelWhen === 'now'} onChange={() => setCancelWhen('now')} className="mt-0.5" data-testid="cancel-now" />
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Imediatamente</p>
                    <p className="text-xs text-slate-500">Cliente cai pro plano Free agora. Fatura já cobrada não é estornada.</p>
                  </div>
                </label>
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-700">Motivo (opcional)</Label>
              <select
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                className="mt-1 w-full h-9 px-3 border border-slate-200 rounded-md text-sm bg-white"
                data-testid="cancel-reason"
              >
                <option value="">Selecione...</option>
                <option value="cliente_cancelou">Cliente desativou o uso</option>
                <option value="reducao_custo">Redução de custos</option>
                <option value="trocou_fornecedor">Trocou de fornecedor</option>
                <option value="fim_parceria">Fim da parceria com o cliente</option>
                <option value="outro">Outro</option>
              </select>
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setCancelTarget(null)} className="flex-1" data-testid="cancel-modal-close">Voltar</Button>
              <Button onClick={handleCancel} disabled={cancelling} className="flex-1 bg-rose-600 hover:bg-rose-700 text-white gap-2" data-testid="confirm-cancel">
                {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Confirmar
              </Button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </main>
  );
};

export default PartnerSponsorshipsTab;
